import { createCRUDHook } from './createCRUDHook';
import {
  NotificationDto,
  UserNotificationDto,
  UserNotificationSettingDto,
  JsonPatch,
  FilterOptionModel,
} from 'utils/models';
import { AxiosResponse } from 'axios';
import {
  TBL_NOTIFICATION,
  TBL_USER_NOTIFICATION,
  TBL_USER_NOTIFICATION_SETTING,
  GET_NOTIFICATION_SETTING_BY_STAFF_ID,
  FILTERED_ENTITIES,
} from './apiEndPoints';
import { prepareQueryParams } from 'utils';

interface NotificationCustomOperations {
  addUserNotificationData: (
    param: UserNotificationDto
  ) => Promise<UserNotificationDto>;

  getUserNotificationByStaffId: (
    filteredEntities: FilterOptionModel
  ) => Promise<UserNotificationDto[]>;

  updateUserNotificationData: (
    param: JsonPatch[],
    id: number
  ) => Promise<AxiosResponse>;

  getNotificationSettingByStaffId: (
    staffId: number
  ) => Promise<UserNotificationSettingDto[]>;

  updateUserNotificationSettingData: (
    param: JsonPatch[],
    id: number
  ) => Promise<AxiosResponse>;
}

const { Context, useManager, useConsumer } = createCRUDHook<
  NotificationDto,
  NotificationDto[],
  NotificationCustomOperations
>({
  entityName: 'Notification',
  endpoint: TBL_NOTIFICATION,

  customOperations: (auth) => ({
    addUserNotificationData: async (
      param: UserNotificationDto
    ): Promise<UserNotificationDto> => {
      try {
        const res = await auth.makeAuthenticatedPost(
          TBL_USER_NOTIFICATION,
          param
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    getUserNotificationByStaffId: async (
      filteredEntities: FilterOptionModel
    ): Promise<UserNotificationDto[]> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          TBL_USER_NOTIFICATION +
            FILTERED_ENTITIES +
            `?${prepareQueryParams(filteredEntities)}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    updateUserNotificationData: async (
      param: JsonPatch[],
      id: number
    ): Promise<AxiosResponse> => {
      try {
        const res = await auth.makeAuthenticatedPatch(
          `${TBL_USER_NOTIFICATION}/${id}`,
          param
        );
        return Promise.resolve(res);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    getNotificationSettingByStaffId: async (
      staffId: number
    ): Promise<UserNotificationSettingDto[]> => {
      try {
        const res = await auth.makeAuthenticatedGet(
          TBL_USER_NOTIFICATION_SETTING +
            GET_NOTIFICATION_SETTING_BY_STAFF_ID +
            `?staffId=${staffId}`
        );
        return Promise.resolve(res.data);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    updateUserNotificationSettingData: async (
      param: JsonPatch[],
      id: number
    ): Promise<AxiosResponse> => {
      try {
        const res = await auth.makeAuthenticatedPatch(
          `${TBL_USER_NOTIFICATION_SETTING}/${id}`,
          param
        );
        return Promise.resolve(res);
      } catch (error) {
        return Promise.reject(error);
      }
    },
  }),
});

export const NotificationContext = Context;
export const useNotificationManager = useManager;
export const useNotification = useConsumer;
